import axios from 'axios';
import { API_BASE_URL, ENABLE_AUTH } from '../utils/constants';
import { clearAuthSession, getAuthSession, setAuthSession } from './authSessionStore';
import { emitDataMutation } from './dataSync';
import { captureClientCrash, captureOperationalAction } from '../monitoring/telemetry';
import {
  exchangeWorkspaceClerkSession,
  exchangeWorkspaceNativeClerkSession,
  fetchWorkspaceWebSession,
  refreshWorkspaceNativeSession,
} from './clerkAuthApi';
import { getClerkRuntime } from './clerkRuntimeStore';
import {
  buildWorkspaceBearerSession,
  buildWorkspaceCookieSession,
  isAuthenticatedWebSession,
  isCookieAuthToken,
} from './browserSession';

const CRITICAL_ACTION_RULES = [
  { method: 'POST', pattern: /^\/admin\/portal-requests\/[^/]+\/approve$/, action: 'admin.portal_request.approve', scope: 'admin' },
  { method: 'POST', pattern: /^\/admin\/portal-requests\/[^/]+\/provision$/, action: 'admin.portal_request.provision', scope: 'admin' },
  { method: 'PATCH', pattern: /^\/admin\/portal-requests\/[^/]+$/, action: 'admin.portal_request.update', scope: 'admin' },
  { method: 'PATCH', pattern: /^\/admin\/tenants\/[^/]+\/users\/[^/]+\/role$/, action: 'tenant.user.role_update', scope: 'tenant' },
  { method: 'POST', pattern: /^\/admin\/tenants\/[^/]+\/credentials$/, action: 'tenant.user.issue_credentials', scope: 'tenant' },
  { method: 'POST', pattern: /^\/admin\/tenants\/[^/]+\/users\/[^/]+\/reset-password$/, action: 'tenant.user.reset_password', scope: 'tenant' },
  { method: 'POST', pattern: /^\/auth\/register$/, action: 'tenant.auth.register', scope: 'tenant' },
  { method: 'POST', pattern: /^\/auth\/login$/, action: 'tenant.auth.login', scope: 'tenant' },
  { method: 'POST', pattern: /^\/auth\/refresh$/, action: 'tenant.auth.refresh', scope: 'tenant' },
  { method: 'POST', pattern: /^\/auth\/logout$/, action: 'tenant.auth.logout', scope: 'tenant' },
  { method: 'POST', pattern: /^\/auth\/change-password$/, action: 'tenant.auth.change_password', scope: 'tenant' },
  { method: 'POST', pattern: /^\/invoices$/, action: 'workspace.invoice.create', scope: 'workspace' },
  { method: 'POST', pattern: /^\/invoices\/[^/]+\/send-email$/, action: 'workspace.invoice.send_email', scope: 'workspace' },
  { method: 'PUT', pattern: /^\/invoices\/[^/]+$/, action: 'workspace.invoice.update', scope: 'workspace' },
  { method: 'POST', pattern: /^\/quotation$/, action: 'workspace.quotation.create', scope: 'workspace' },
  { method: 'POST', pattern: /^\/quotation\/[^/]+\/send-email$/, action: 'workspace.quotation.send_email', scope: 'workspace' },
  { method: 'PUT', pattern: /^\/quotation\/[^/]+$/, action: 'workspace.quotation.update', scope: 'workspace' },
  { method: 'DELETE', pattern: /^\/quotation\/[^/]+$/, action: 'workspace.quotation.delete', scope: 'workspace' },
  { method: 'POST', pattern: /^\/estimated-invoices$/, action: 'workspace.estimated_invoice.create', scope: 'workspace' },
  { method: 'POST', pattern: /^\/estimated-invoices\/[^/]+\/send-email$/, action: 'workspace.estimated_invoice.send_email', scope: 'workspace' },
  { method: 'PUT', pattern: /^\/estimated-invoices\/[^/]+$/, action: 'workspace.estimated_invoice.update', scope: 'workspace' },
  { method: 'DELETE', pattern: /^\/estimated-invoices\/[^/]+$/, action: 'workspace.estimated_invoice.delete', scope: 'workspace' },
  { method: 'POST', pattern: /^\/products$/, action: 'workspace.product.create', scope: 'workspace' },
  { method: 'PUT', pattern: /^\/products\/[^/]+$/, action: 'workspace.product.update', scope: 'workspace' },
  { method: 'DELETE', pattern: /^\/products\/[^/]+$/, action: 'workspace.product.delete', scope: 'workspace' },
  { method: 'POST', pattern: /^\/customers$/, action: 'workspace.customer.create', scope: 'workspace' },
  { method: 'PUT', pattern: /^\/customers\/[^/]+$/, action: 'workspace.customer.update', scope: 'workspace' },
  { method: 'PATCH', pattern: /^\/customers\/[^/]+$/, action: 'workspace.customer.patch', scope: 'workspace' },
  { method: 'DELETE', pattern: /^\/customers\/[^/]+$/, action: 'workspace.customer.delete', scope: 'workspace' },
  { method: 'POST', pattern: /^\/taxes$/, action: 'workspace.tax.create', scope: 'workspace' },
  { method: 'DELETE', pattern: /^\/taxes\/[^/]+$/, action: 'workspace.tax.delete', scope: 'workspace' },
  { method: 'POST', pattern: /^\/settings$/, action: 'workspace.settings.update', scope: 'workspace' },
  { method: 'POST', pattern: /^\/field-controller\/preferences$/, action: 'workspace.field_controller.update', scope: 'workspace' },
  { method: 'POST', pattern: /^\/payments$/, action: 'workspace.payment.create', scope: 'workspace' },
  { method: 'POST', pattern: /^\/campaigns\/templates$/, action: 'workspace.campaign.template_create', scope: 'workspace' },
  { method: 'PUT', pattern: /^\/campaigns\/templates\/[^/]+$/, action: 'workspace.campaign.template_update', scope: 'workspace' },
  { method: 'DELETE', pattern: /^\/campaigns\/templates\/[^/]+$/, action: 'workspace.campaign.template_archive', scope: 'workspace' },
  { method: 'POST', pattern: /^\/campaigns\/preview$/, action: 'workspace.campaign.preview', scope: 'workspace' },
  { method: 'POST', pattern: /^\/campaigns\/send$/, action: 'workspace.campaign.send', scope: 'workspace' },
  { method: 'POST', pattern: /^\/document-layouts$/, action: 'workspace.document_layout.create', scope: 'workspace' },
  { method: 'PUT', pattern: /^\/document-layouts\/[^/]+$/, action: 'workspace.document_layout.update', scope: 'workspace' },
  { method: 'POST', pattern: /^\/document-layouts\/[^/]+\/publish$/, action: 'workspace.document_layout.publish', scope: 'workspace' },
  { method: 'POST', pattern: /^\/document-layouts\/[^/]+\/duplicate$/, action: 'workspace.document_layout.duplicate', scope: 'workspace' },
  { method: 'DELETE', pattern: /^\/document-layouts\/[^/]+$/, action: 'workspace.document_layout.archive', scope: 'workspace' },
  { method: 'POST', pattern: /^\/document-layout-assets$/, action: 'workspace.document_layout_asset.upload', scope: 'workspace' },
];

function toPathname(url = '') {
  if (!url) return '';
  return String(url).split('?')[0];
}

function matchCriticalAction(method = 'GET', url = '') {
  const upperMethod = String(method || '').toUpperCase();
  const pathname = toPathname(url);

  const matchedRule = CRITICAL_ACTION_RULES.find(
    (rule) => rule.method === upperMethod && rule.pattern.test(pathname)
  );

  if (!matchedRule) {
    return null;
  }

  return {
    action: matchedRule.action,
    scope: matchedRule.scope,
    path: pathname,
    method: upperMethod,
  };
}

function shouldSkipAuthRefresh(pathname = '') {
  if (!pathname) return true;
  if (/^\/auth\/(login|register|refresh|logout)$/.test(pathname)) return true;
  if (/^\/auth\/native\/(login|refresh|logout)$/.test(pathname)) return true;
  if (/^\/auth\/web\/(login|logout|session)$/.test(pathname)) return true;
  if (/^\/auth\/(clerk\/exchange|web\/clerk\/exchange|native\/clerk\/exchange)$/.test(pathname)) return true;
  if (/^\/admin-auth(\/|$)/.test(pathname)) return true;
  if (/^\/admin(\/|$)/.test(pathname)) return true;
  return false;
}

function buildNextSessionFromRefreshResult(result = {}, authSource = 'local') {
  const currentSession = getAuthSession() || {};
  if (result?.accessToken && !result?.authenticated) {
    return buildWorkspaceBearerSession(result, currentSession, authSource);
  }

  return buildWorkspaceCookieSession(result, currentSession, authSource);
}

function isJsonEnvelope(payload) {
  return (
    payload &&
    typeof payload === 'object' &&
    Object.prototype.hasOwnProperty.call(payload, 'success') &&
    Object.prototype.hasOwnProperty.call(payload, 'data')
  );
}

function extractResourceEnvelope(response) {
  const payload = response?.data;

  if (isJsonEnvelope(payload)) {
    return {
      data: payload.data,
      meta: payload.meta || {},
      envelope: payload,
    };
  }

  return {
    data: payload,
    meta: {},
    envelope: null,
  };
}

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: ENABLE_AUTH,
});

let refreshPromise = null;

function refreshAccessTokenSingleFlight() {
  if (!refreshPromise) {
    const currentSession = getAuthSession() || {};
    const isBearerSession =
      currentSession?.authTransport === 'bearer' ||
      (currentSession?.accessToken && !isCookieAuthToken(currentSession.accessToken));

    if (isBearerSession && currentSession?.authSource === 'clerk') {
      const clerkRuntime = getClerkRuntime();
      refreshPromise = Promise.resolve()
        .then(async () => {
          if (!clerkRuntime?.workspaceEnabled || !clerkRuntime?.isSignedIn) {
            throw new Error('Unable to refresh Clerk-backed session');
          }

          const clerkToken = await clerkRuntime.getToken();
          if (!clerkToken) {
            throw new Error('Unable to refresh Clerk-backed session');
          }

          const result = await exchangeWorkspaceNativeClerkSession({
            clerkToken,
            tenantId: currentSession?.tenantId,
          });
          if (!result?.accessToken) {
            throw new Error('Unable to refresh Clerk-backed session');
          }

          const nextSession = buildWorkspaceBearerSession(result, currentSession, 'clerk');
          setAuthSession(nextSession);
          return nextSession.accessToken || null;
        })
        .catch((refreshError) => {
          clearAuthSession();
          throw refreshError;
        })
        .finally(() => {
          refreshPromise = null;
        });
    } else if (currentSession?.authSource === 'clerk') {
      const clerkRuntime = getClerkRuntime();
      refreshPromise = Promise.resolve()
        .then(async () => {
          if (!clerkRuntime?.workspaceEnabled || !clerkRuntime?.isSignedIn) {
            throw new Error('Unable to refresh Clerk-backed session');
          }

          const clerkToken = await clerkRuntime.getToken();
          if (!clerkToken) {
            throw new Error('Unable to refresh Clerk-backed session');
          }

          const result = await exchangeWorkspaceClerkSession({
            clerkToken,
            tenantId: currentSession?.tenantId,
          });
          if (!isAuthenticatedWebSession(result)) {
            throw new Error('Unable to refresh Clerk-backed session');
          }

          const nextSession = buildNextSessionFromRefreshResult(result, 'clerk');
          setAuthSession(nextSession);
          return nextSession.accessToken || null;
        })
        .catch((refreshError) => {
          clearAuthSession();
          throw refreshError;
        })
        .finally(() => {
          refreshPromise = null;
        });
    } else if (isBearerSession) {
      refreshPromise = Promise.resolve()
        .then(async () => {
          if (!currentSession?.refreshToken) {
            throw new Error('Unable to refresh session');
          }

          const result = await refreshWorkspaceNativeSession({
            refreshToken: currentSession.refreshToken,
          });
          if (!result?.accessToken) {
            throw new Error('Unable to refresh session');
          }

          const nextSession = buildWorkspaceBearerSession(result, currentSession, currentSession?.authSource || 'local');
          setAuthSession(nextSession);
          return nextSession.accessToken || null;
        })
        .catch((refreshError) => {
          clearAuthSession();
          throw refreshError;
        })
        .finally(() => {
          refreshPromise = null;
        });
    } else {
      refreshPromise = Promise.resolve()
        .then(() => fetchWorkspaceWebSession())
        .then((result) => {
          if (!isAuthenticatedWebSession(result)) {
            throw new Error('Unable to refresh session');
          }

          const nextSession = buildNextSessionFromRefreshResult(result, 'local');
          setAuthSession(nextSession);
          return nextSession.accessToken || null;
        })
        .catch((refreshError) => {
          clearAuthSession();
          throw refreshError;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
  }

  return refreshPromise;
}

client.interceptors.request.use((config) => {
  config.withCredentials = ENABLE_AUTH;
  config.metadata = {
    ...(config.metadata || {}),
    startedAt: Date.now(),
  };
  const session = getAuthSession();
  if (session?.accessToken && !isCookieAuthToken(session.accessToken)) {
    config.headers.Authorization = `Bearer ${session.accessToken}`;
  }
  if (session?.tenantId) {
    config.headers['x-tenant-id'] = session.tenantId;
  }
  return config;
});

client.interceptors.response.use(
  (response) => {
    const criticalAction = matchCriticalAction(response?.config?.method, response?.config?.url);
    if (criticalAction) {
      captureOperationalAction({
        ...criticalAction,
        status: 'success',
        statusCode: response?.status,
        durationMs: Date.now() - Number(response?.config?.metadata?.startedAt || Date.now()),
      });
    }

    const method = String(response?.config?.method || 'get').toLowerCase();
    if (!['get', 'head', 'options'].includes(method)) {
      emitDataMutation({
        method,
        url: response?.config?.url || '',
        status: response?.status,
      });
    }
    return response;
  },
  (error) => {
    const originalRequestConfig = error?.config || {};
    const pathname = toPathname(originalRequestConfig?.url || '');
    const shouldRetryWithRefresh =
      ENABLE_AUTH &&
      error?.response?.status === 401 &&
      !originalRequestConfig?.skipAuthRefresh &&
      !originalRequestConfig?.__tenantAuthRetry &&
      !shouldSkipAuthRefresh(pathname) &&
      Boolean(getAuthSession()?.accessToken || originalRequestConfig?.headers?.Authorization);

    if (shouldRetryWithRefresh) {
      originalRequestConfig.__tenantAuthRetry = true;
      return refreshAccessTokenSingleFlight().then((nextAccessToken) => {
        originalRequestConfig.headers = { ...(originalRequestConfig.headers || {}) };
        if (nextAccessToken && !isCookieAuthToken(nextAccessToken)) {
          originalRequestConfig.headers.Authorization = `Bearer ${nextAccessToken}`;
        } else if (originalRequestConfig.headers.Authorization) {
          delete originalRequestConfig.headers.Authorization;
        }
        return client.request(originalRequestConfig);
      });
    }

    const criticalAction = matchCriticalAction(error?.config?.method, error?.config?.url);
    if (criticalAction) {
      captureOperationalAction({
        ...criticalAction,
        status: 'failure',
        statusCode: error?.response?.status || 0,
        durationMs: Date.now() - Number(error?.config?.metadata?.startedAt || Date.now()),
      });
    }

    if (
      pathname !== '/telemetry/client-errors' &&
      (!error?.response || Number(error?.response?.status || 0) >= 500)
    ) {
      captureClientCrash(error, {
        source: 'api.response',
        apiPath: pathname,
        method: String(error?.config?.method || 'GET').toUpperCase(),
        statusCode: Number(error?.response?.status || 0),
        errorCode: error?.response?.data?.error?.code || '',
      });
    }

    const responseError = error?.response?.data?.error;
    if (responseError?.message) {
      error.message = responseError.message;
    }
    if (responseError?.code) {
      error.code = responseError.code;
    }
    if (error?.response?.data) {
      error.payload = error.response.data;
    }
    return Promise.reject(error);
  }
);

async function requestResource(requestPromise) {
  const response = await requestPromise;
  const resource = extractResourceEnvelope(response);

  return {
    data: resource.data,
    meta: resource.meta,
    status: response?.status,
    headers: response?.headers || {},
  };
}

export function getResource(url, config) {
  return requestResource(client.get(url, config));
}

export function postResource(url, payload, config) {
  return requestResource(client.post(url, payload, config));
}

export function putResource(url, payload, config) {
  return requestResource(client.put(url, payload, config));
}

export function patchResource(url, payload, config) {
  return requestResource(client.patch(url, payload, config));
}

export function deleteResource(url, config) {
  return requestResource(client.delete(url, config));
}

export async function getJson(url, config) {
  return (await getResource(url, config)).data;
}

export async function postJson(url, payload, config) {
  return (await postResource(url, payload, config)).data;
}

export async function putJson(url, payload, config) {
  return (await putResource(url, payload, config)).data;
}

export async function patchJson(url, payload, config) {
  return (await patchResource(url, payload, config)).data;
}

export async function deleteJson(url, config) {
  return (await deleteResource(url, config)).data;
}

export { extractResourceEnvelope };

export default client;
